import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api/api.service";
import { Loader, CheckCircle, X } from "lucide-react";

const PriceInfo = ({ uniqueTitle, onPriceUpdate }) => {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState("");
  const [buyLoading, setBuyLoading] = useState(false);
  const [buySuccess, setBuySuccess] = useState(false);
  const [buyError, setBuyError] = useState(null);

  // State variables for price update functionality
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [priceData, setPriceData] = useState({
    buyPrice: "",
    high52Week: "",
    low52Week: "",
    highAllTime: "",
    lowAllTime: "",
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success"); // success, error

  const navigate = useNavigate();
  const modalRef = useRef(null);

  // Toast notification function
  const showToastNotification = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  useEffect(() => {
    // Check if user is admin
    const checkAdminStatus = async () => {
      try {
        const userInfo = await apiService.getUserInfo();
        setIsAdmin(userInfo?.role === "admin");
      } catch (err) {
        console.error("Failed to check admin status:", err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const result = await apiService.getProductByTitle(uniqueTitle);
        if (result?.success && result?.data) {
          setItem(result.data);
          // Populate the price update form with current values
          setPriceData({
            buyPrice: result.data.weekData52?.buyPrice || "",
            high52Week: result.data.weekData52?.high52Week || "",
            low52Week: result.data.weekData52?.low52Week || "",
            highAllTime: result.data.allTime?.high || "",
            lowAllTime: result.data.allTime?.low || "",
          });
        } else {
          throw new Error("No data available");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (uniqueTitle) {
      fetchProduct();
    }
  }, [uniqueTitle]);

  // Handle clicks outside modal to close it
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (showUpdateModal) setShowUpdateModal(false);
        if (showBuyModal) setShowBuyModal(false);
      }
    };

    if (showUpdateModal || showBuyModal) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showUpdateModal, showBuyModal]);

  useEffect(() => {
    // Fetch user portfolios when buy modal is opened
    const fetchUserPortfolios = async () => {
      try {
        const response = await apiService.getUserHoldings();

        if (Array.isArray(response)) {
          setPortfolios(response);
          if (response.length > 0) {
            setSelectedPortfolioId(response[0]._id || response[0].portfolioId);
          }
        } else if (response && response.data) {
          const portfolioData = Array.isArray(response.data)
            ? response.data
            : [response.data];
          setPortfolios(portfolioData);
          if (portfolioData.length > 0) {
            setSelectedPortfolioId(
              portfolioData[0]._id || portfolioData[0].portfolioId
            );
          }
        }
      } catch (err) {
        console.error("Failed to fetch portfolios:", err);
        setBuyError("Failed to load portfolios. Please try again.");
      }
    };

    if (showBuyModal) {
      fetchUserPortfolios();
    }
  }, [showBuyModal]);

  const handleBuyClick = () => {
    setShowBuyModal(true);
    setBuySuccess(false);
    setBuyError(null);
  };

  const handleUpdateClick = () => {
    // Reinitialize price data from the current item state to ensure form has latest values
    if (item) {
      setPriceData({
        buyPrice: item.weekData52?.buyPrice || "",
        high52Week: item.weekData52?.high52Week || "",
        low52Week: item.weekData52?.low52Week || "",
        highAllTime: item.allTime?.high || "",
        lowAllTime: item.allTime?.low || "",
      });
    }
    setShowUpdateModal(true);
    setUpdateSuccess(false);
    setUpdateError(null);
  };

  const handlePriceInputChange = (e) => {
    const { name, value } = e.target;
    setPriceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdatePriceData = async (e) => {
    e.preventDefault();

    try {
      setUpdateLoading(true);
      setUpdateError(null);

      // Convert form values to numbers for API
      const numericPriceData = {
        buyPrice: parseFloat(priceData.buyPrice),
        high52Week: parseFloat(priceData.high52Week),
        low52Week: parseFloat(priceData.low52Week),
        highAllTime: parseFloat(priceData.highAllTime),
        lowAllTime: parseFloat(priceData.lowAllTime),
      };

      // Structure the update data according to your backend expectations
      const updateData = {
        weekData52: {
          buyPrice: numericPriceData.buyPrice,
          high52Week: numericPriceData.high52Week,
          low52Week: numericPriceData.low52Week,
          lastUpdate: new Date().toISOString(),
        },
        allTime: {
          high: numericPriceData.highAllTime,
          low: numericPriceData.lowAllTime,
        },
      };

      // Call the API endpoint to update the product
      const response = await apiService.updateProduct(uniqueTitle, updateData);

      if (response?.success) {
        setUpdateSuccess(true);

        // Update the local item state with the new values
        const updatedItem = {
          ...item,
          weekData52: {
            ...item.weekData52,
            buyPrice: numericPriceData.buyPrice,
            high52Week: numericPriceData.high52Week,
            low52Week: numericPriceData.low52Week,
            lastUpdate: updateData.weekData52.lastUpdate,
          },
          allTime: {
            ...item.allTime,
            high: numericPriceData.highAllTime,
            low: numericPriceData.lowAllTime,
          },
        };

        setItem(updatedItem);

        // Notify parent component about the update
        if (onPriceUpdate) {
          onPriceUpdate(updatedItem);
        }

        // Show toast notification
        showToastNotification("Price updated successfully!", "success");

        // Close modal after a short delay
        setTimeout(() => {
          setShowUpdateModal(false);
        }, 1000);
      } else {
        throw new Error(response?.message || "Failed to update price data");
      }
    } catch (err) {
      const errorMessage =
        err.message ||
        "Failed to update price data.";
      setUpdateError(errorMessage);
      showToastNotification(errorMessage, "error");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleAddHolding = async (e) => {
    e.preventDefault();

    if (!selectedPortfolioId) {
      setBuyError("Please select a portfolio");
      return;
    }

    try {
      setBuyLoading(true);
      setBuyError(null);

      // Structure the holding data according to your backend expectations
      const holdingData = {
        productId: item._id || uniqueTitle,
        productTitle: item.title,
        productName: item.title,
        quantity: parseInt(quantity),
        buyPrice: parseFloat(item.weekData52?.buyPrice) || 0,
      };

      const result = await apiService.addHolding(
        selectedPortfolioId,
        holdingData
      );

      setBuySuccess(true);
      showToastNotification("Holding added successfully!", "success");

      setTimeout(() => {
        setShowBuyModal(false);
        navigate(`/portfolio/${selectedPortfolioId}`);
      }, 1500);
    } catch (err) {
      const errorMessage =
        err.message ||
        "Failed to add holding.";
      setBuyError(errorMessage);
      showToastNotification(errorMessage, "error");
    } finally {
      setBuyLoading(false);
    }
  };

  if (loading)
    return <Loader className="animate-spin text-indigo-500 mb-4" size={36} />;
  if (error)
    return <div className="p-4 text-center text-red-500">Error: {error}</div>;
  if (!item) return <div className="p-4 text-center">No data available</div>;

  const { weekData52, allTime } = item;

  return (
    <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-6">
              <h2 className="text-2xl font-bold">
                ₹{weekData52?.buyPrice || "N/A"}
              </h2>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              52W High: ₹{weekData52?.high52Week || "N/A"}
            </p>
            <p className="text-xs text-gray-500">
              52W Low: ₹{weekData52?.low52Week || "N/A"}
            </p>
            <p className="text-xs text-gray-500">
              All Time High: ₹{allTime?.high || "N/A"}
            </p>
            <p className="text-xs text-gray-500">
              All Time Low: ₹{allTime?.low || "N/A"}
            </p>
            <p className="text-xs text-gray-500">
              Last updated:{" "}
              {weekData52?.lastUpdate
                ? new Date(weekData52.lastUpdate).toLocaleDateString()
                : "N/A"}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleUpdateClick}
              className="bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Update Price
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg min-w-80 ${
              toastType === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            <div className="flex items-center space-x-2 flex-1">
              {toastType === "success" ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <X className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">{toastMessage}</span>
            </div>
            <button
              onClick={() => setShowToast(false)}
              className="text-white hover:text-gray-200 focus:outline-none"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                Update Price Data for {item.title}
              </h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdatePriceData}>
              {updateError && (
                <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
                  {updateError}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Current Buy Price (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="buyPrice"
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={priceData.buyPrice}
                  onChange={handlePriceInputChange}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  52 Week High (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="high52Week"
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={priceData.high52Week}
                  onChange={handlePriceInputChange}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  52 Week Low (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="low52Week"
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={priceData.low52Week}
                  onChange={handlePriceInputChange}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  All Time High (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="highAllTime"
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={priceData.highAllTime}
                  onChange={handlePriceInputChange}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  All Time Low (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="lowAllTime"
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={priceData.lowAllTime}
                  onChange={handlePriceInputChange}
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  onClick={() => setShowUpdateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  disabled={updateLoading}
                >
                  {updateLoading ? "Updating..." : "Update Price Data"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in-right {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PriceInfo;
